import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEvents } from '../contexts/EventContext';
import { useAuth } from '../contexts/AuthContext';
import { isAtLeast } from '../utils/roles';
import { EventSetupWizard } from './events/EventSetupWizard';

/**
 * EventSwitcher — the visible "which event am I working on" control.
 *
 * Shows the selected event (and its venue) and opens a dropdown to switch
 * between the tenant's events or create a new one. Selecting an event re-scopes
 * every data page (guests, seating, orders) via the X-Event-Id header + a
 * keyed remount of the routed subtree in AppLayout.
 *
 * Styled for the dark sidebar/header surface. Create is gated to Organizer+.
 */
export function EventSwitcher() {
  const { events, selectedEvent, selectedEventId, selectEvent, loading } = useEvents();
  const { user } = useAuth();
  const navigate = useNavigate();
  const canCreate = isAtLeast(user?.role || '', 'Organizer');

  // Selecting an event takes you to that event's dashboard, so you always land
  // somewhere oriented to the event you just chose.
  function chooseEvent(id: string) {
    selectEvent(id);
    setOpen(false);
    navigate('/dashboard');
  }

  const [open, setOpen] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const label = selectedEvent?.name || (loading ? 'Loading…' : 'No event selected');
  const venueName = selectedEvent?.venue?.name;

  return (
    <div ref={rootRef} style={styles.root}>
      <div style={styles.caption}>Active event</div>
      <button
        type="button"
        style={styles.trigger}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={label}
      >
        <span style={styles.triggerIcon} aria-hidden>🗓️</span>
        <span style={styles.triggerText}>
          <span style={styles.triggerName}>{label}</span>
          {venueName && <span style={styles.triggerVenue}>{venueName}</span>}
        </span>
        <span style={styles.caret} aria-hidden>{open ? '▴' : '▾'}</span>
      </button>

      {open && (
        <div style={styles.menu} role="listbox" aria-label="Select event">
          {events.length === 0 && !loading && (
            <div style={styles.empty}>No events yet.</div>
          )}
          {events.map((ev) => {
            const active = ev.id === selectedEventId;
            return (
              <button
                key={ev.id}
                type="button"
                role="option"
                aria-selected={active}
                style={{ ...styles.option, ...(active ? styles.optionActive : {}) }}
                onClick={() => chooseEvent(ev.id)}
              >
                <span style={styles.optionCheck} aria-hidden>{active ? '✓' : ''}</span>
                <span style={styles.optionText}>
                  <span style={styles.optionName}>{ev.name}</span>
                  {ev.venue?.name && <span style={styles.optionVenue}>{ev.venue.name}</span>}
                </span>
              </button>
            );
          })}

          {canCreate && (
            <div style={styles.createZone}>
              <button
                type="button"
                style={styles.createBtn}
                onClick={() => { setWizardOpen(true); setOpen(false); }}
              >
                ＋ Create event
              </button>
              <div style={styles.hint}>A short guided setup picks the right features for your event.</div>
            </div>
          )}
        </div>
      )}

      {wizardOpen && <EventSetupWizard onClose={() => setWizardOpen(false)} />}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  root: {
    position: 'relative',
    marginBottom: '16px',
  },
  caption: {
    fontSize: '10px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: '#6b8572',
    fontWeight: 700,
    padding: '0 4px 6px',
  },
  trigger: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    width: '100%',
    padding: '8px 10px',
    backgroundColor: 'rgba(201,168,76,0.08)',
    border: '1px solid rgba(201,168,76,0.25)',
    borderRadius: '8px',
    cursor: 'pointer',
    textAlign: 'left',
    color: '#f0f4f1',
  },
  triggerIcon: { fontSize: '16px', flexShrink: 0 },
  triggerText: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' },
  triggerName: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#f0f4f1',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  triggerVenue: {
    fontSize: '11px',
    color: '#c9a84c',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  caret: { fontSize: '12px', color: '#c9a84c', flexShrink: 0 },
  menu: {
    position: 'absolute',
    top: 'calc(100% + 4px)',
    left: 0,
    right: 0,
    backgroundColor: '#0f2d1f',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: '8px',
    boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
    padding: '6px',
    zIndex: 300,
    maxHeight: '320px',
    overflowY: 'auto',
  },
  empty: { padding: '10px', fontSize: '12px', color: '#6b8572', textAlign: 'center' },
  option: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    width: '100%',
    padding: '8px 8px',
    background: 'none',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    textAlign: 'left',
    color: '#a3b8a9',
  },
  optionActive: {
    background: 'linear-gradient(135deg, rgba(26,107,60,0.3), rgba(201,168,76,0.12))',
    color: '#c9a84c',
  },
  optionCheck: { width: '14px', fontSize: '12px', color: '#c9a84c', flexShrink: 0 },
  optionText: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' },
  optionName: {
    fontSize: '13px',
    fontWeight: 500,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  optionVenue: { fontSize: '11px', color: '#6b8572' },
  createZone: {
    borderTop: '1px solid rgba(255,255,255,0.08)',
    marginTop: '6px',
    paddingTop: '6px',
  },
  createBtn: {
    width: '100%',
    padding: '8px',
    background: 'none',
    border: '1px dashed rgba(201,168,76,0.4)',
    borderRadius: '6px',
    color: '#c9a84c',
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  createForm: { display: 'flex', flexDirection: 'column', gap: '6px', padding: '2px' },
  input: {
    width: '100%',
    padding: '8px 10px',
    backgroundColor: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: '6px',
    color: '#f0f4f1',
    fontSize: '13px',
    boxSizing: 'border-box',
  },
  createActions: { display: 'flex', gap: '6px' },
  saveBtn: {
    flex: 1,
    padding: '8px',
    backgroundColor: '#1a6b3c',
    border: 'none',
    borderRadius: '6px',
    color: '#fff',
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  cancelBtn: {
    flex: 1,
    padding: '8px',
    backgroundColor: 'rgba(255,255,255,0.08)',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: '6px',
    color: '#a3b8a9',
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  hint: { fontSize: '10px', color: '#6b8572', lineHeight: 1.3 },
  error: {
    marginTop: '6px',
    padding: '6px 8px',
    backgroundColor: 'rgba(211,47,47,0.15)',
    border: '1px solid rgba(211,47,47,0.4)',
    borderRadius: '6px',
    color: '#ff8a80',
    fontSize: '11px',
  },
};
